import { Category, ICategoryData } from '@df/catalog/category/category';
import { CategoryResource } from '@df/catalog/category/category.resource';
import { TLoadDependeciesParams } from '@df/catalog/model';
import { Meta } from '@df/core/meta/meta';
import { NgInject } from '@df/ng/ng-inject';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { NCmsOutletBlock } from '../../../cms/outlet/block/cms-outlet-block.interface';
import { CATEGORY_QUICK_LINKS_CONTENT_BLOCK_URL } from './../../ui/category/category-quick-links.component';
import { NCategoryHeader } from './../../ui/category/header/category-header.interface';

declare module '@df/catalog/category/category' {
    interface ICategoryDataV3 {
        alt_view_allowed?: 1 | 0;
        alt_view_default?: 1 | 0;
        alt_view_name?: string;
        columns_l?: number;
        columns_m?: number;
        columns_s?: number;
        main_view_name?: string;
        klevu_breadcrumbs?: string;
        use_magento_merchandising?: boolean;
        explore_links: IExploreLinks[];
        quick_links: IQuickLink[];
        use_quick_links_on_facets: boolean;
    }
}

declare module '@df/catalog/model' {
    interface IMetaData {
        explore_links?: IFacetExploreLinks[];
        level?: number;
        quick_links?: IFacetQuickLink[];
        _rule?: number;
    }
}

export interface IFacetExploreLinks {
    count?: number;
    label?: string;
    link?: string;
}

export interface IExploreLinks {
    count: number;
    label: string[];
    url: string[];
}

export interface IQuickLink {
    initialize: boolean;
    label: string;
    link: string;
    record_id: number;
}

export interface IFacetQuickLink {
    label: string;
    link: string;
    record_id: number;
}

export enum ECategoryId {
    madeToOrder = 556,
    preOrderNow = 557
}

export class CategoryDecorator<T extends ICategoryData = ICategoryData> extends Category<T> {
    @NgInject(ICmsBlockResource)
    private iCmsBlockResource!: ICmsBlockResource;

    @NgInject(Meta)
    private meta!: Meta;

    /**
     * Getter for category header block id
     */
    get cmsBlockHeaderVariant(): string {
        return 'category-header' + this.id;
    }

    get cmsBlockFooterVariant(): string {
        return 'category.' + this.id;
    }

    get categoryHeaderVariant(): string {
        return 'category' + this.id;
    }

    get rule(): number | undefined {
        return this.meta?.state?.metaData?._rule;
    }

    get cmsBlockCategoryQuickLinksVariant(): string {
        return `category-quick-links${this.id}`;
    }

    get cmsBlockQuickLinksVariant(): string {
        if (this.meta?.state?.metaData?.level && this.rule) {
            return `${this.cmsBlockCategoryQuickLinksVariant}-${this.rule}`;
        }

        return this.cmsBlockCategoryQuickLinksVariant;
    }

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);

        const loader = params.loader;

        if (this.verbosity > 2) {
            [this.cmsBlockHeaderVariant, this.cmsBlockFooterVariant].forEach(i =>
                loader.add(this.iCmsBlockResource.retrieve({ bareUrl: NCmsOutletBlock.BLOCK_ID, variant: i }, params))
            );

            loader.add(
                this.iCmsBlockResource.retrieve({ bareUrl: NCategoryHeader.ID, variant: this.categoryHeaderVariant }, params)
            );

            loader.add(
                this.iCmsBlockResource.retrieve({
                    bareUrl: CATEGORY_QUICK_LINKS_CONTENT_BLOCK_URL,
                    variant: this.cmsBlockQuickLinksVariant
                }, params)
            );
        }
    }
}

CategoryResource.instance = CategoryDecorator;
